import React, { useState } from "react";
import PhoneField from "../../components/common/PhoneField";
import { useSelector, useDispatch } from "react-redux";
import { registerUser } from "../user/userSlice";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  isEmail,
  isRequired,
  isValidPassword,
  isPlz,
  isPhone,
  PASSWORD_MIN_LENGTH,
  PASSWORD_HINT,
} from "../../utils/validation";

export default function Main() {
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [strasse, setStrasse] = useState("");
  const [plz, setPlz] = useState("");
  const [ort, setOrt] = useState("");
  const [land, setLand] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleRegistration = (e) => {
    e.preventDefault();
    if (loading) return;

    const nextErrors = {};
    if (!isRequired(vorname)) nextErrors.vorname = "Bitte Vorname eingeben.";
    if (!isRequired(nachname)) nextErrors.nachname = "Bitte Nachname eingeben.";
    if (!isRequired(phone)) nextErrors.phone = "Bitte Telefonnummer eingeben.";
    else if (!isPhone(phone))
      nextErrors.phone = "Bitte eine gültige Telefonnummer eingeben.";
    if (!isRequired(email)) nextErrors.email = "Bitte E-Mail eingeben.";
    else if (!isEmail(email))
      nextErrors.email = "Bitte eine gültige E-Mail-Adresse eingeben.";
    if (!isValidPassword(password))
      nextErrors.password = `Passwort: ${PASSWORD_HINT.toLowerCase()}.`;
    if (isRequired(plz) && !isPlz(plz)) nextErrors.plz = "PLZ: 4–5 Ziffern.";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setLoading(true);

    const user = {
      email,
      password,
      vorname,
      nachname,
      telefonnummer: phone,
      strasse,
      plz,
      ort,
      land,
    };
    dispatch(registerUser(user))
      .unwrap()
      .then(() => {
        navigate("/verify-email");
      })
      .catch((err) => {
        toast.error(err.message || "Email bereits verwendet");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="kn-page">
      {loading && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="mx-2 mt-1 h-[2px] bg-gray-300 opacity-80 rounded-full animate-pulse" />
        </div>
      )}

      <div className="mx-auto w-full max-w-[820px] px-6 pt-14">
        <div className="kn-eyebrow">Konto</div>
        <h1 className="kn-h1 mt-3.5">Erstelle jetzt einen Account</h1>
      </div>

      <main className="mx-auto w-full max-w-[820px] px-6 pt-9 pb-14">
        <form
          onSubmit={handleRegistration}
          className="flex flex-col gap-[34px]"
          noValidate
        >
          {/* 1 · Person */}
          <fieldset className="border-0 p-0 m-0">
            <legend className="kn-legend mb-4 p-0">1 · Person</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="kn-field-label">
                  Vorname
                  <input
                    className="kn-input mt-1.5"
                    value={vorname}
                    onChange={(e) => setVorname(e.target.value)}
                    placeholder="Vorname"
                    autoComplete="given-name"
                    aria-invalid={!!errors.vorname}
                  />
                </label>
                {errors.vorname && (
                  <p className="text-sm text-knapp-accent mt-1">
                    {errors.vorname}
                  </p>
                )}
              </div>
              <div>
                <label className="kn-field-label">
                  Nachname
                  <input
                    className="kn-input mt-1.5"
                    value={nachname}
                    onChange={(e) => setNachname(e.target.value)}
                    placeholder="Nachname"
                    autoComplete="family-name"
                    aria-invalid={!!errors.nachname}
                  />
                </label>
                {errors.nachname && (
                  <p className="text-sm text-knapp-accent mt-1">
                    {errors.nachname}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <div className="kn-field-label">
                  <span>Telefon</span>
                  <div className="mt-1.5">
                    <PhoneField
                      value={phone}
                      onChange={setPhone}
                      invalid={Boolean(errors.phone)}
                    />
                  </div>
                </div>
                {errors.phone && (
                  <p className="text-sm text-knapp-accent mt-1">
                    {errors.phone}
                  </p>
                )}
              </div>
            </div>
          </fieldset>

          {/* 2 · Adresse */}
          <fieldset className="border-0 p-0 m-0">
            <legend className="kn-legend mb-4 p-0">2 · Adresse</legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-3">
                <label className="kn-field-label">
                  Strasse und Nr.
                  <input
                    className="kn-input mt-1.5"
                    value={strasse}
                    onChange={(e) => setStrasse(e.target.value)}
                    placeholder="Straße"
                    autoComplete="street-address"
                  />
                </label>
              </div>
              <div className="sm:col-span-1">
                <label className="kn-field-label">
                  PLZ
                  <input
                    className="kn-input mt-1.5"
                    value={plz}
                    onChange={(e) => setPlz(e.target.value)}
                    placeholder="PLZ"
                    autoComplete="postal-code"
                    aria-invalid={!!errors.plz}
                  />
                </label>
                {errors.plz && (
                  <p className="text-sm text-knapp-accent mt-1">{errors.plz}</p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="kn-field-label">
                  Ort
                  <input
                    className="kn-input mt-1.5"
                    value={ort}
                    onChange={(e) => setOrt(e.target.value)}
                    placeholder="Ort"
                    autoComplete="address-level2"
                  />
                </label>
              </div>
              <div className="sm:col-span-3">
                <label className="kn-field-label">
                  Land
                  <select
                    className="kn-input mt-1.5 cursor-pointer"
                    value={land}
                    onChange={(e) => setLand(e.target.value)}
                    autoComplete="country-name"
                  >
                    <option value="Schweiz">Schweiz</option>
                    <option value="Deutschland">Deutschland</option>
                    <option value="Österreich">Österreich</option>
                  </select>
                </label>
              </div>
            </div>
          </fieldset>

          {/* 3 · Zugangsdaten */}
          <fieldset className="border-0 p-0 m-0">
            <legend className="kn-legend mb-4 p-0">3 · Zugangsdaten</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="kn-field-label">
                  E-Mail
                  <input
                    className="kn-input mt-1.5"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="E-Mail"
                    type="email"
                    autoComplete="email"
                    aria-invalid={!!errors.email}
                  />
                </label>
                {errors.email && (
                  <p className="text-sm text-knapp-accent mt-1">
                    {errors.email}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="kn-field-label">
                  Passwort
                  <span className="mt-1.5 flex items-center rounded-[10px] border border-knapp-borderInput bg-white focus-within:border-knapp-ink transition-colors">
                    <input
                      className="font-barlow text-base text-knapp-ink px-4 py-3 border-0 bg-transparent outline-none flex-1 min-w-0 placeholder:text-knapp-placeholder rounded-[10px]"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Passwort"
                      autoComplete="new-password"
                      aria-invalid={!!errors.password}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="border-0 bg-transparent text-sm font-semibold text-knapp-ink3 px-4 py-3 cursor-pointer whitespace-nowrap hover:text-knapp-ink"
                      aria-label={
                        showPassword
                          ? "Passwort verbergen"
                          : "Passwort anzeigen"
                      }
                      aria-pressed={showPassword}
                    >
                      {showPassword ? "Verbergen" : "Anzeigen"}
                    </button>
                  </span>
                </label>
                {errors.password ? (
                  <p className="text-sm text-knapp-accent mt-1">
                    {errors.password}
                  </p>
                ) : (
                  <p className="text-[13px] text-knapp-ink4 mt-1">
                    {PASSWORD_HINT}
                  </p>
                )}
              </div>
            </div>
          </fieldset>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-knapp-border pt-6">
            <button
              type="submit"
              disabled={loading}
              className="kn-btn-primary py-3.5 px-16 text-base"
            >
              {loading ? "Registrieren…" : "Registrieren"}
            </button>
            <span className="text-[15px] text-knapp-ink2">
              Du hast schon ein Konto?{" "}
              <Link to="/login" className="font-semibold underline">
                Anmelden
              </Link>
            </span>
          </div>
        </form>
      </main>
    </div>
  );
}
